import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SimpleModal from "../Modals/SimpleModal";
import {
  SavedFilterPreset,
  SavedFiltersPayload,
} from "../../slices/user-settings";
import { constants } from "../../utils/constants/general";

interface DeleteConfirmModalProps {
  open: boolean;
  presetName: string;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  open,
  presetName,
  saving,
  onConfirm,
  onCancel,
}) => (
  <SimpleModal
    open={open}
    handleClose={onCancel}
    handleAccept={onConfirm}
    title={"Delete saved filter"}
    acceptLabel={"Delete"}
    acceptButtonColor={"error"}
    acceptButtonLoading={saving}
    minWidth={"400px"}
    maxWidth={"480px"}
    zIndex={constants.zIndexes.drawerModal}
  >
    <Box sx={{ py: 1 }}>
      <Typography variant={"body2"}>
        Are you sure you want to delete the preset{" "}
        <Box component={"span"} sx={{ fontWeight: 600 }}>
          “{presetName}”
        </Box>
        ?
      </Typography>
      <Typography variant={"caption"} color={"text.secondary"} sx={{ mt: 1, display: "block" }}>
        This action can't be undone.
      </Typography>
    </Box>
  </SimpleModal>
);

interface ManagePresetsModalProps<TFilters> {
  open: boolean;
  saving: boolean;
  payload: SavedFiltersPayload<TFilters>;
  isNameTaken: (name: string, excludeId?: string) => boolean;
  onRename: (id: string, name: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  onSetDefault: (id: string | null) => Promise<void>;
  onClose: () => void;
}

const MAX_NAME_LEN = 60;

function ManagePresetsModal<TFilters>(
  props: ManagePresetsModalProps<TFilters>,
) {
  const {
    open,
    saving,
    payload,
    isNameTaken,
    onRename,
    onRemove,
    onSetDefault,
    onClose,
  } = props;

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingError, setEditingError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setEditingName("");
      setEditingError(null);
      setConfirmDeleteId(null);
    }
  }, [open]);

  const confirmDeletePreset = confirmDeleteId
    ? payload.presets.find((p) => p.id === confirmDeleteId) ?? null
    : null;

  const beginEdit = (preset: SavedFilterPreset<TFilters>) => {
    setEditingId(preset.id);
    setEditingName(preset.name);
    setEditingError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingError(null);
  };

  const commitEdit = async () => {
    if (!editingId) return;
    const trimmed = editingName.trim();
    if (!trimmed) {
      setEditingError("Name is required.");
      return;
    }
    if (trimmed.length > MAX_NAME_LEN) {
      setEditingError(`Name must be ${MAX_NAME_LEN} characters or fewer.`);
      return;
    }
    if (isNameTaken(trimmed, editingId)) {
      setEditingError("A preset with this name already exists.");
      return;
    }
    const idBeingEdited = editingId;
    await onRename(idBeingEdited, trimmed);
    setEditingId(null);
    setEditingName("");
    setEditingError(null);
  };

  const handleSetDefault = (id: string) => {
    if (saving) return;
    if (payload.defaultId === id) {
      onSetDefault(null);
    } else {
      onSetDefault(id);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    const idToRemove = confirmDeleteId;
    await onRemove(idToRemove);
    setConfirmDeleteId(null);
  };

  return (
    <>
    <SimpleModal
      open={open}
      handleClose={onClose}
      title={"Manage saved filters"}
      acceptLabel={"Done"}
      handleAccept={onClose}
      disableCancelButton
      minWidth={"520px"}
      maxWidth={"600px"}
      loading={saving}
      zIndex={constants.zIndexes.drawerModal}
    >
      {payload.presets.length === 0 ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography variant={"body2"} color={"text.secondary"}>
            You don&apos;t have any saved filters yet.
          </Typography>
        </Box>
      ) : (
        <Stack divider={<Box sx={{ height: "1px", bgcolor: "divider" }} />}>
          {payload.presets.map((preset) => {
            const isEditing = editingId === preset.id;
            const isDefault = payload.defaultId === preset.id;
            return (
              <Box
                key={preset.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  py: 1.25,
                }}
              >
                <Tooltip
                  title={isDefault ? "Clear default" : "Set as default"}
                  placement={"top"}
                >
                  <span>
                    <Radio
                      color={"secondary"}
                      size={"small"}
                      checked={isDefault}
                      onClick={() => handleSetDefault(preset.id)}
                      disabled={saving}
                    />
                  </span>
                </Tooltip>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <TextField
                      autoFocus
                      fullWidth
                      size={"small"}
                      value={editingName}
                      onChange={(e) => {
                        setEditingName(e.target.value);
                        if (editingError) setEditingError(null);
                      }}
                      inputProps={{ maxLength: MAX_NAME_LEN }}
                      error={Boolean(editingError)}
                      helperText={editingError ?? undefined}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitEdit();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant={"body2"}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: isDefault ? 600 : 400,
                        }}
                      >
                        {preset.name}
                      </Typography>
                      {isDefault && (
                        <Typography
                          variant={"caption"}
                          color={"secondary"}
                          sx={{ fontWeight: 600 }}
                        >
                          DEFAULT
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {isEditing ? (
                    <>
                      <Tooltip title={"Save"} placement={"top"}>
                        <span>
                          <IconButton
                            size={"small"}
                            onClick={commitEdit}
                            disabled={saving}
                          >
                            <CheckIcon fontSize={"small"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={"Cancel"} placement={"top"}>
                        <span>
                          <IconButton
                            size={"small"}
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            <CloseIcon fontSize={"small"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title={"Rename"} placement={"top"}>
                        <span>
                          <IconButton
                            size={"small"}
                            onClick={() => beginEdit(preset)}
                            disabled={saving}
                          >
                            <EditIcon fontSize={"small"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={"Delete"} placement={"top"}>
                        <span>
                          <IconButton
                            size={"small"}
                            onClick={() => setConfirmDeleteId(preset.id)}
                            disabled={saving}
                          >
                            <DeleteOutlineIcon fontSize={"small"} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </SimpleModal>
    <DeleteConfirmModal
      open={Boolean(confirmDeletePreset)}
      presetName={confirmDeletePreset?.name ?? ""}
      saving={saving}
      onConfirm={handleConfirmDelete}
      onCancel={() => setConfirmDeleteId(null)}
    />
    </>
  );
}

export default ManagePresetsModal;
